import React, { useState, useEffect, useCallback } from "react";
import Personal from "../Personal/Personal";
import JobS from "../Job/Job";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import "./ParentComponent.scss";
import {
  ExperimentOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import Reporting from "./Reporting";
import Skills from "./Skills";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { useNavigate } from "react-router-dom";
import { message } from "antd";

function HumanResource() {
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { jobApplicantId } = useParams();

  const navigate = useNavigate();

  // Centralized state to store all form data from each step
  const [allFormData, setAllFormData] = useState({
    step1: {}, // Personal info
    step2: {}, // Employment details
    step3: {}, // Reporting structure
    step4: {}, // Skills
  });

  // Function to update data for a specific step
  const updateStepData = useCallback((stepNumber, data) => {
    setAllFormData((prev) => {
      const updatedData = {
        ...prev,
        [`step${stepNumber}`]: data,
      };

      return updatedData;
    });
  }, []);

  const submitAllData = useCallback(
    async (finalStepData) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        const formData = new FormData();

        // --- Image ---
        const imageFile = allFormData.step1.image;
        if (imageFile instanceof File) {
          formData.append("Image", imageFile);
        } else {
          console.warn("Image is not a valid File object:", imageFile);
        }

        // --- Basic Fields ---
        formData.append("JobApplicantId", parseInt(jobApplicantId));
        formData.append("WorkEmail", allFormData.step1.workEmail || "");

        formData.append("Employment.HiringDate", allFormData.step2.hiringDate || "");
        formData.append(
          "Employment.ProbationStartDate",
          allFormData.step2.probationStartDate || ""
        );
        formData.append("Employment.ProbationEndDate", allFormData.step2.probationEndDate || "");
        formData.append("Employment.DesignationId", Number(allFormData.step2.designationId || 0));
        formData.append("Employment.BaseSalary", parseFloat(allFormData.step2.baseSalary || 0));
        formData.append("PriorExperienceYears", parseFloat(allFormData.step2.priorExperience || 0));

        // --- Reporting ---
        if (allFormData.step3.selectedSupervisor?.id) {
          formData.append("Reporting.SupervisorId", allFormData.step3.selectedSupervisor.id);
        }
        formData.append(
          "Reporting.ReassignDirectReports",
          allFormData.step3.placementOption === "PARENT"
        );

        // Skills and domain
        const domainIds = finalStepData?.domainIds || [];
        const skillIds = finalStepData.skillIds || [];
        const customSkills = finalStepData.customSkills || [];

        domainIds.forEach((id,index) => {
          formData.append(`Skills.DomainKnowledgeIds[${index}]`,Number(id));
        })

        // Append even if empty
        skillIds.forEach((id, index) => {
          formData.append(`Skills.SkillIds[${index}]`, Number(id));
        });

        customSkills.forEach((skill, index) => {
          formData.append(`Skills.CustomSkills[${index}]`, skill);
        });

        const response = await axiosInstance.post(API.employee.create, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        message.success("Employee created successfully!");
        navigate("/signIn");
      } catch (error) {
        console.error("Submission Error Details:", error.response?.data || error);

        const displayMessage = error.response?.data?.details || "Submission failed!";
        message.error(displayMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [allFormData, jobApplicantId, navigate, isSubmitting]
  );

  // Navigate to next step and store current step data
  const handleNextPage = useCallback((stepData) => {
      updateStepData(currentPage, stepData);

      if (currentPage === 4) {
        submitAllData(stepData);
      } else {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    },
    [currentPage, updateStepData, submitAllData]
  );

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prevPage) => prevPage - 1);
  }, []);

  // Save form data to localStorage for persistence
  useEffect(() => {
    localStorage.setItem("employeeFormData", JSON.stringify(allFormData));
  }, [allFormData]);

  // Load form data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem("employeeFormData");
    if (savedData) {
      try {
        setAllFormData(JSON.parse(savedData));
      } catch (error) {
        console.error("Error parsing saved form data:", error);
      }
    }
  }, []);

  return (
    <DashboardLayout>
      <div className="wrapper d-flex flex-column min-vh-100 bg-light">
        <DashboardNavbar />
        <div className="body flex-grow-1 px-3">
          <div className="stepper-wrapper">
            <div className={`stepper-item initial ${currentPage >= 1 ? "completed" : ""}`}>
              <div className={`step-counter ${currentPage === 1 ? "active" : ""}`}>
                <UserOutlined />
              </div>
              <div className="step-name">Personal</div>
            </div>
            <div className={`stepper-item ${currentPage >= 2 ? "completed" : ""}`}>
              <div className={`step-counter ${currentPage === 2 ? "active" : ""}`}>
                <SolutionOutlined />
              </div>
              <div className="step-name">Employment</div>
            </div>
            <div className={`stepper-item ${currentPage >= 3 ? "completed" : ""}`}>
              <div className={`step-counter ${currentPage === 3 ? "active" : ""}`}>
                <TeamOutlined />
              </div>
              <div className="step-name">Reporting</div>
            </div>
            <div className={`stepper-item ${currentPage >= 4 ? "completed" : ""}`}>
              <div className={`step-counter ${currentPage === 4 ? "active" : ""}`}>
                <ExperimentOutlined />
              </div>
              <div className="step-name">Skills</div>
            </div>
          </div>

          {currentPage === 1 && (
            <Personal onNext={handleNextPage} initialData={allFormData.step1} />
          )}

          {currentPage === 2 && (
            <JobS
              onNext={handleNextPage}
              onBack={handlePreviousPage}
              initialData={allFormData.step2}
              jobData={allFormData.step1} // Pass the data from Personal step which includes departmentId
            />
          )}

          {currentPage === 3 && (
            <Reporting
              onNext={handleNextPage}
              onBack={handlePreviousPage}
              initialData={allFormData.step3}
              jobData={{
                ...allFormData.step1, // departmentId, fullName
                ...allFormData.step2, // designationId, designationName
              }}
            />
          )}

          {currentPage === 4 && (
            <Skills
  onNext={handleNextPage}
  onBack={handlePreviousPage}
  initialData={allFormData.step4}
  isSubmitting={isSubmitting}
  departmentId={allFormData.step2.departmentId || allFormData.step1.departmentId}
/>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default HumanResource;
